import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Download, Edit } from 'lucide-react';
import Pagination from 'react-responsive-pagination';
import 'react-responsive-pagination/themes/classic.css';
import AppLayout from '../components/AppLayout';
import TarionBulkUpdateModal from '../components/TarionBulkUpdateModal';
import { tarionService } from '../services/tarionService';
import { TarionUnit, TarionFilters } from '../types/tarion';
import api from '../services/api';
import { toast } from 'react-toastify';
import '../styles/Builders.css';
import '../styles/Buttons.css';
import '../styles/DataTable.css';
import '../styles/Contacts.css';

interface PaginationData {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

interface Project {
  _id: string;
  name: string;
}

const Tarion: React.FC = () => {
  const navigate = useNavigate();
  const { workspaceId } = useParams<{ workspaceId: string }>();

  const [units, setUnits] = useState<TarionUnit[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [dataLoading, setDataLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [filters, setFilters] = useState<TarionFilters>({});
  const [pagination, setPagination] = useState<PaginationData>({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });

  // Selection state
  const [selectedUnits, setSelectedUnits] = useState<Set<string>>(new Set());
  const [showBulkUpdateModal, setShowBulkUpdateModal] = useState(false);
  const isFetchingRef = useRef(false);

  useEffect(() => {
    if (workspaceId) {
      fetchProjects();
    }
  }, [workspaceId]);

  useEffect(() => {
    if (workspaceId && projects.length > 0 && !filters.projectId) {
      // Set default project filter to first project if not already set
      setFilters({ ...filters, projectId: projects[0]._id });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId, projects]);

  // Debounce search query
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 500); // 500ms delay

    return () => clearTimeout(debounceTimer);
  }, [searchQuery]);

  useEffect(() => {
    if (workspaceId && filters.projectId && !isFetchingRef.current) {
      // Use initial loading for first load, data refresh for subsequent loads
      const isInitialLoad = loading && pagination.page === 1;
      fetchUnits(!isInitialLoad);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page, pagination.limit, debouncedSearchQuery, filters]);

  const fetchProjects = async () => {
    if (!workspaceId) return;

    try {
      const response = await api.get(`/workspaces/${workspaceId}/projects`, {
        params: {
          limit: 1000,
          sortBy: 'name',
          sortOrder: 'asc'
        }
      });
      setProjects(response.data.projects);

      // If no projects, stop loading
      if (!response.data.projects || response.data.projects.length === 0) {
        setLoading(false);
      }
    } catch (err: any) {
      console.error('Error fetching projects:', err);
      toast.error('Failed to load projects');
      setLoading(false);
    }
  };

  const fetchUnits = async (isDataRefresh: boolean = false) => {
    if (!workspaceId) {
      setLoading(false);
      return;
    }

    // Prevent duplicate calls
    if (isFetchingRef.current) {
      return;
    }

    // If no projectId selected, don't fetch and stop loading
    if (!filters.projectId) {
      setLoading(false);
      setDataLoading(false);
      return;
    }

    try {
      isFetchingRef.current = true;

      // Use dataLoading for data refreshes (pagination, search), loading for initial load
      if (isDataRefresh) {
        setDataLoading(true);
      } else {
        setLoading(true);
      }
      setError('');

      const response = await tarionService.getAll(
        workspaceId,
        { ...filters, search: debouncedSearchQuery },
        pagination.page,
        pagination.limit
      );

      setUnits(response.units);

      // Only update pagination if values actually changed to avoid triggering useEffect
      const newPagination = response.pagination;
      if (
        newPagination.page !== pagination.page ||
        newPagination.limit !== pagination.limit ||
        newPagination.total !== pagination.total ||
        newPagination.pages !== pagination.pages
      ) {
        setPagination(newPagination);
      }

      // Clear selections when data changes
      setSelectedUnits(new Set());
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Failed to fetch Tarion data';
      setError(errorMessage);
      console.error('Error fetching Tarion data:', err);
      toast.error(errorMessage);
    } finally {
      if (isDataRefresh) {
        setDataLoading(false);
      } else {
        setLoading(false);
      }
      isFetchingRef.current = false;
    }
  };

  const handleBackToWorkspaces = () => {
    navigate('/workspaces');
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setPagination({ ...pagination, page: 1 });
  };

  const handleProjectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setFilters({ ...filters, projectId: e.target.value });
    setPagination({ ...pagination, page: 1 });
  };

  const handlePageChange = (page: number) => {
    setPagination({ ...pagination, page });
  };

  const handleLimitChange = (limit: number) => {
    setPagination({ ...pagination, limit, page: 1 }); // Reset to page 1 when changing limit
  };

  const handleExport = async () => {
    if (!workspaceId) return;

    try {
      const blob = await tarionService.exportData(workspaceId, { ...filters, search: debouncedSearchQuery });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `tarion-report-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Tarion report exported successfully');
    } catch (err: any) {
      console.error('Error exporting Tarion data:', err);
      toast.error('Failed to export Tarion report');
    }
  };

  // Selection handlers
  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      // Only select units that have deals
      const unitsWithDeals = units.filter(u => u.hasDeal).map(u => u._id);
      setSelectedUnits(new Set(unitsWithDeals));
    } else {
      setSelectedUnits(new Set());
    }
  };

  const handleSelectUnit = (unitId: string) => {
    const newSelected = new Set(selectedUnits);
    if (newSelected.has(unitId)) {
      newSelected.delete(unitId);
    } else {
      newSelected.add(unitId);
    }
    setSelectedUnits(newSelected);
  };

  const handleBulkUpdate = async (dates: any) => {
    if (!workspaceId || selectedUnits.size === 0) return;

    try {
      const result = await tarionService.bulkUpdateOccupancyDates(
        workspaceId,
        Array.from(selectedUnits),
        dates
      );

      toast.success(`Successfully updated ${result.modifiedCount} unit(s)`);
      setShowBulkUpdateModal(false);
      setSelectedUnits(new Set());
      fetchUnits(); // Refresh the data
    } catch (err: any) {
      console.error('Error bulk updating:', err);
      toast.error(err.response?.data?.message || 'Failed to update occupancy dates');
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const unitsWithDealsCount = units.filter(u => u.hasDeal).length;
  const allUnitsWithDealsSelected = unitsWithDealsCount > 0 && selectedUnits.size === unitsWithDealsCount;

  if (!workspaceId) {
    return (
      <AppLayout onBack={handleBackToWorkspaces} backLabel="Workspaces">
        <div className="builders-container">
          <div className="builders-error">
            <p>No workspace selected</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout onBack={handleBackToWorkspaces} backLabel="Workspaces">
      <>
        <div className="builders-container">
          {/* Section Header */}
          <div className="builders-section-header">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h2 className="builders-section-title">Tarion</h2>
                <p className="builders-section-subtitle">
                  Manage Tarion warranty and occupancy dates
                </p>
              </div>
              <div style={{ display: 'flex', gap: '10px' }}>
                {selectedUnits.size > 0 && (
                  <button
                    className="btn btn-primary btn-md"
                    onClick={() => setShowBulkUpdateModal(true)}
                  >
                    <Edit size={18} />
                    Update {selectedUnits.size} Unit{selectedUnits.size > 1 ? 's' : ''}
                  </button>
                )}
                <button className="btn btn-outline btn-md" onClick={handleExport}>
                  <Download size={18} />
                  Export
                </button>
              </div>
            </div>
          </div>

          {/* White Card Container */}
          <div style={{
            backgroundColor: '#FFFFFF',
            borderRadius: '12px',
            padding: '24px',
            boxShadow: '0 4px 12px rgba(72, 24, 47, 0.15), 0 2px 4px rgba(72, 24, 47, 0.08)'
          }}>
            {/* Filters */}
            <div className="builders-actions" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px', marginBottom: '20px' }}>
              <select
                className="form-input"
                value={filters.projectId || ''}
                onChange={handleProjectChange}
              >
                <option value="">Select Project</option>
                {projects.map((project) => (
                  <option key={project._id} value={project._id}>
                    {project.name}
                  </option>
                ))}
              </select>

              <input
                type="text"
                className="form-input"
                placeholder="Search units..."
                value={searchQuery}
                onChange={handleSearchChange}
              />
            </div>

            {error && (
              <div className="builders-error" style={{ marginBottom: '20px' }}>
                <p>{error}</p>
              </div>
            )}

            {loading ? (
              <div className="builders-loading">
                <div className="builders-loading-spinner" />
              </div>
            ) : units.length === 0 ? (
              <div className="builders-empty">
                <p className="builders-empty-title">No units found</p>
                <p className="builders-empty-text">
                  {filters.projectId ? 'No units available for the selected project' : 'Please select a project to view units'}
                </p>
              </div>
            ) : (
              <>
                <div className="data-table-container" style={{ marginBottom: '20px', position: 'relative' }}>
                  {/* Loading Overlay */}
                  {dataLoading && (
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: 'rgba(255, 255, 255, 0.8)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      zIndex: 10
                    }}>
                      <div className="contacts-loading-spinner"></div>
                    </div>
                  )}

                  <table className="data-table">
                    <thead>
                      <tr>
                        <th style={{ width: '50px' }}>
                          <input
                            type="checkbox"
                            checked={allUnitsWithDealsSelected}
                            onChange={handleSelectAll}
                            disabled={unitsWithDealsCount === 0 || dataLoading}
                          />
                        </th>
                        <th>Unit</th>
                        <th>Has Deal</th>
                        <th>Purchasers</th>
                        <th>Tentative 1 Occupancy</th>
                        <th>Tentative 2 Occupancy</th>
                        <th>Last Penalty Date</th>
                        <th>Final Occupancy</th>
                        <th>Firm Occupancy</th>
                        <th>Possession Date</th>
                      </tr>
                    </thead>
                    <tbody style={{ opacity: dataLoading ? 0.5 : 1, transition: 'opacity 0.2s' }}>
                      {units.map((unit) => (
                        <tr key={unit._id} style={{ cursor: dataLoading ? 'wait' : 'default' }}>
                          <td>
                            <input
                              type="checkbox"
                              checked={selectedUnits.has(unit._id)}
                              onChange={() => handleSelectUnit(unit._id)}
                              disabled={!unit.hasDeal}
                            />
                          </td>
                          <td>
                            <strong>{unit.unitNo}</strong>
                          </td>
                          <td>
                            <span className={`status-badge ${unit.hasDeal ? 'status-active' : 'status-inactive'}`}>
                              {unit.hasDeal ? 'Yes' : 'No'}
                            </span>
                          </td>
                          <td>
                            {unit.purchasers && unit.purchasers.length > 0 ? (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                {unit.purchasers.map((purchaser, index) => (
                                  <div key={index}>{purchaser}</div>
                                ))}
                              </div>
                            ) : (
                              '—'
                            )}
                          </td>
                          <td>{formatDate(unit.tentative1Occupancy)}</td>
                          <td>{formatDate(unit.tentative2Occupancy)}</td>
                          <td>{formatDate(unit.lastPenaltyDate)}</td>
                          <td>{formatDate(unit.finalOccupancy)}</td>
                          <td>{formatDate(unit.firmOccupancy)}</td>
                          <td>{formatDate(unit.possessionDate)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination and Records Per Page */}
                <div className="contacts-pagination-controls">
                  {/* Records Per Page Selector */}
                  <div className="contacts-records-per-page">
                    <span>Show</span>
                    <select
                      value={pagination.limit}
                      onChange={(e) => handleLimitChange(Number(e.target.value))}
                      disabled={dataLoading}
                    >
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                    <span>records per page (Total: {pagination.total})</span>
                  </div>

                  {/* Pagination */}
                  {pagination.pages > 1 && (
                    <div className="contacts-pagination-wrapper" style={{ pointerEvents: dataLoading ? 'none' : 'auto', opacity: dataLoading ? 0.6 : 1 }}>
                      <Pagination
                        current={pagination.page}
                        total={pagination.pages}
                        onPageChange={handlePageChange}
                        maxWidth={400}
                      />
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Bulk Update Modal */}
        {showBulkUpdateModal && (
          <TarionBulkUpdateModal
            selectedCount={selectedUnits.size}
            onUpdate={handleBulkUpdate}
            onCancel={() => setShowBulkUpdateModal(false)}
          />
        )}
      </>
    </AppLayout>
  );
};

export default Tarion;
